import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { HiOutlineCheckCircle, HiOutlineXCircle, HiOutlineClock } from 'react-icons/hi';

export default function GuestAccessPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [firmaData, setFirmaData] = useState(null);
  const [expiresAt, setExpiresAt] = useState(null);
  const [offers, setOffers] = useState([]);

  useEffect(() => {
    validateToken();
  }, [token]);

  const validateToken = async () => {
    try {
      setLoading(true);
      const response = await fetch(`/api/guest-access/${token}`);
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Link geçersiz');
      }
      
      const data = await response.json();
      setFirmaData(data.firma);
      setExpiresAt(new Date(data.expiresAt));
      setOffers(data.offers || []);
      
      // Kullanıcıyı otomatik giriş yap
      localStorage.setItem('teklifFirmaInfo', JSON.stringify({
        firma: data.firma.firma,
        vergiDairesi: data.firma.vergiDairesi,
        vergiNo: data.firma.vergiNo,
        yetkili: data.firma.yetkili,
        telefon: data.firma.telefon,
        email: data.firma.email,
        sehir: data.firma.sehir,
        bilgilerIletildi: true,
        firmaId: data.firma.id
      }));
      
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleContinue = () => {
    navigate('/teklif-olustur');
  };

  const formatExpiryTime = (date) => {
    const now = new Date();
    const diff = date - now;
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    
    if (days > 0) {
      return `${days} gün ${hours} saat`;
    } else if (hours > 0) {
      return `${hours} saat`;
    } else {
      return '1 saatten az';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-r from-blue-50 to-indigo-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-blue-600 font-medium">Link doğrulanıyor...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-r from-red-50 to-pink-100">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full mx-4">
          <div className="text-center">
            <HiOutlineXCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-red-600 mb-4">Link Geçersiz</h1>
            <p className="text-gray-600 mb-6">{error}</p>
            <button
              onClick={() => navigate('/dash')}
              className="bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700 transition"
            >
              Ana Sayfaya Dön
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-r from-green-50 to-blue-100">
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-2xl mx-auto">
          {/* Başarı Mesajı */}
          <div className="bg-white rounded-2xl shadow-xl p-8 mb-6">
            <div className="text-center">
              <HiOutlineCheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h1 className="text-3xl font-bold text-green-600 mb-2">Hoş Geldiniz!</h1>
              <p className="text-gray-600 text-lg">Link başarıyla doğrulandı</p>
            </div>
          </div>

          {/* Firma Bilgileri */}
          <div className="bg-white rounded-2xl shadow-xl p-8 mb-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Firma Bilgileri</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">Firma Adı</label>
                <p className="text-gray-800 font-medium">{firmaData.firma}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">Yetkili Kişi</label>
                <p className="text-gray-800 font-medium">{firmaData.yetkili}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">Email</label>
                <p className="text-gray-800 font-medium">{firmaData.email}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">Telefon</label>
                <p className="text-gray-800 font-medium">{firmaData.telefon}</p>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-600 mb-1">Adres</label>
                <p className="text-gray-800 font-medium">{firmaData.adres}</p>
              </div>
            </div>
          </div>

          {/* Süre Bilgisi */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6 mb-6">
            <div className="flex items-center gap-3 mb-3">
              <HiOutlineClock className="w-6 h-6 text-yellow-600" />
              <h3 className="text-lg font-semibold text-yellow-800">Link Süresi</h3>
            </div>
            <p className="text-yellow-700">
              Bu link <strong>{formatExpiryTime(expiresAt)}</strong> daha geçerlidir.
            </p>
          </div>

          {/* Sipariş Listesi */}
          {offers.length > 0 && (
            <div className="bg-white rounded-2xl shadow-xl p-8 mb-6">
              <h2 className="text-2xl font-bold text-gray-800 mb-6">Sipariş Geçmişi</h2>
              <div className="space-y-4">
                {offers.map((offer, index) => (
                  <div key={offer.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <h3 className="font-semibold text-gray-800">Sipariş #{offer.id}</h3>
                        <p className="text-sm text-gray-500">
                          {new Date(offer.created_at).toLocaleString('tr-TR')}
                        </p>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                        offer.status === 'completed' 
                          ? 'bg-green-100 text-green-800'
                          : offer.status === 'pending'
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {offer.status === 'completed' ? '✅ Tamamlandı' :
                         offer.status === 'pending' ? '⏳ Beklemede' :
                         offer.status === 'cancelled' ? '❌ İptal' :
                         '📝 Yeni'}
                      </span>
                    </div>
                    {offer.items && (
                      <div className="text-sm text-gray-600">
                        <p><strong>Ürün Sayısı:</strong> {JSON.parse(offer.items).length} adet</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Devam Butonu */}
          <div className="text-center">
            <button
              onClick={handleContinue}
              className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-8 py-4 rounded-xl font-bold text-lg hover:from-blue-700 hover:to-purple-700 transition-all duration-300 shadow-lg hover:shadow-xl"
            >
              {offers.length > 0 ? 'Yeni Sipariş Oluştur' : 'Sipariş Oluşturma Ekranına Devam Et'}
            </button>
            <p className="text-gray-500 text-sm mt-4">
              Tüm firma bilgileriniz otomatik olarak doldurulmuştur
            </p>
          </div>
        </div>
      </div>
    </div>
  );
} 